// src/components/FollowUpSettings.js
// « Relances » — what happens to a case waiting on a carrier or a customer,
// said in the account's mode (AgentWakeupSettings, top card):
// - pilot / live — the AI agent re-examines the case on its own (« Réveil
//   IA »). The table's delays are MAXIMUM delays before it looks again
//   (Backend agent_wakeup_service: account_delay_cap, « DÉLAIS DU COMPTE » in
//   its prompt; 72 h while waiting for the carrier by default, the platform's
//   5 days elsewhere), never an order to write: following up is its
//   judgement under the platform rule (a carrier that answered is never
//   chased to insist, a silent one may be, spaced). The send hour does not
//   apply to the agent.
// - off — the mail service's timers: the « toujours en attente » notice and
//   the re-send of our last request to a carrier that never answered, once a
//   day at the send hour, a rule = the delay between two follow-ups.
// - The timers also run side by side in pilot: their send hour stays
//   editable there. In live they run on no ticket of the agent's channels,
//   whatever its age (KI-311, Kevin 03/11: « les relances ne sont pas
//   automatiques, elles doivent être gérées par l'agent IA »).
// Saved values are never lost: unknown carriers / statuses / delays stay
// selectable, and the stored send hour is sent back unchanged.
// API: GET/PUT /api/logistics/followup-config {rules, available_statuses,
// default_delay_hours, send_hour, default_send_hour}.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  FaPlus, FaTrash, FaSpinner, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaInfoCircle,
} from 'react-icons/fa';
import './FollowUpSettings.css';
import AgentWakeupSettings from './AgentWakeupSettings';
import { EDN_STATUS_VALUES, getStatusLabel } from '../constants/ticketStatuses';

const ALL_CARRIERS = '*';
const WAITING_CARRIER = 'waiting_carrier_response';
const DEFAULT_DELAY_HOURS = 72;
const DEFAULT_SEND_HOUR = 7;
const AGENT_MAX_HOURS = 72; // agent_wakeup_service.MAX_DELAY_HOURS (Kevin 30/10: 72 h, week-end compris)
const SEND_HOURS = Array.from({ length: 24 }, (_, hour) => hour);
// The agent re-examines from 1 h to 5 days; the timers send once a day, so
// their delays are whole days, up to the 30 days they follow a case.
export const AGENT_DELAYS = [4, 8, 12, 24, 36, 48, 72];
export const TIMER_DELAYS = [24, 48, 72, 96, 120, 168, 240, 336, 504, 720];

const formatHour = (hour) => `${String(hour).padStart(2, '0')}:00`;

const FollowUpSettings = () => {
  const { t, i18n } = useTranslation();
  const [rules, setRules] = useState(null); // null = loading
  const [carriers, setCarriers] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState(EDN_STATUS_VALUES);
  const [defaultDelayHours, setDefaultDelayHours] = useState(DEFAULT_DELAY_HOURS);
  const [sendHour, setSendHour] = useState(''); // '' = platform default
  const [defaultSendHour, setDefaultSendHour] = useState(DEFAULT_SEND_HOUR);
  // undefined = not known yet, null = unreadable, else off / pilot / live
  const [mode, setMode] = useState(undefined);
  const [agentMaxHours, setAgentMaxHours] = useState(AGENT_MAX_HOURS);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const feedbackTimer = useRef(null);

  const statusLabel = (status) => getStatusLabel(t, status);

  const showFeedback = useCallback((type, message) => {
    clearTimeout(feedbackTimer.current);
    setFeedback({ type, message });
    if (type === 'success') {
      feedbackTimer.current = setTimeout(() => setFeedback(null), 5000);
    }
  }, []);

  useEffect(() => () => clearTimeout(feedbackTimer.current), []);

  // Stable: AgentWakeupSettings reloads when it changes.
  const handleModeChange = useCallback((nextMode, data) => {
    setMode(nextMode);
    const max = Number(data?.bounds_hours?.[1]);
    if (max > 0) setAgentMaxHours(max);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
        const [configResponse, carriersResponse] = await Promise.all([
          axios.get('/api/logistics/followup-config', { headers }),
          axios.get('/api/logistics/carriers', { headers }),
        ]);
        setRules(configResponse.data.rules || []);
        if (configResponse.data.available_statuses?.length) {
          setAvailableStatuses(configResponse.data.available_statuses);
        }
        if (configResponse.data.default_delay_hours) {
          setDefaultDelayHours(configResponse.data.default_delay_hours);
        }
        if (Number.isInteger(configResponse.data.default_send_hour)) {
          setDefaultSendHour(configResponse.data.default_send_hour);
        }
        setSendHour(
          Number.isInteger(configResponse.data.send_hour) ? String(configResponse.data.send_hour) : ''
        );
        setCarriers((carriersResponse.data || []).map((carrier) => carrier.name));
      } catch (error) {
        console.error('Failed to fetch follow-up settings:', error?.response?.status || error?.message);
        setRules([]);
        showFeedback('error', t('followUpSettings.loadError', 'Impossible de charger les paramètres de relance.'));
      }
    };
    fetchData();
  }, [t, showFeedback]);

  const agentFollows = mode === 'pilot' || mode === 'live';

  const markDirty = () => { setIsDirty(true); setFeedback(null); };

  const handleAddRule = () => {
    setRules([
      ...(rules || []),
      { carrier: ALL_CARRIERS, status: WAITING_CARRIER, enabled: true, delay_hours: defaultDelayHours },
    ]);
    markDirty();
  };

  const handleRuleChange = (index, field, value) => {
    const updated = [...rules];
    updated[index] = { ...updated[index], [field]: value };
    setRules(updated);
    markDirty();
  };

  const handleRemoveRule = (index) => {
    const updated = [...rules];
    updated.splice(index, 1);
    setRules(updated);
    markDirty();
  };

  // Live duplicate detection — every index whose (carrier, status) pair
  // appears more than once gets highlighted.
  const ruleKey = (rule) => `${(rule.carrier || '').trim().toLowerCase()}|${rule.status}`;
  const duplicateIndexes = (() => {
    const byKey = new Map();
    (rules || []).forEach((rule, index) => {
      const key = ruleKey(rule);
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(index);
    });
    const dupes = new Set();
    byKey.forEach((indexes) => {
      if (indexes.length > 1) indexes.forEach((i) => dupes.add(i));
    });
    return dupes;
  })();

  const days = (hours) => {
    const value = Math.round((Number(hours) / 24) * 10) / 10;
    return Number.isInteger(value) ? String(value) : value.toLocaleString(i18n?.language || 'fr');
  };

  // « 72 h (3 j) » — and, for the agent, what a delay beyond its 72 h means.
  const delayLabel = (hours) => {
    const h = Number(hours);
    if (!h) return String(hours ?? '');
    if (agentFollows && h > agentMaxHours) {
      return t('followUpSettings.overAgentMax', '{{hours}} h — {{max}} h au plus pour l’agent', { hours: h, max: agentMaxHours });
    }
    return h >= 24
      ? t('followUpSettings.hoursDays', '{{hours}} h ({{days}} j)', { hours: h, days: days(h) })
      : t('followUpSettings.hours', '{{hours}} h', { hours: h });
  };

  // Constrained choices; a stored value outside them stays selectable.
  const delayOptions = (current) => {
    const base = agentFollows ? AGENT_DELAYS : TIMER_DELAYS;
    const value = Number(current);
    return value > 0 && !base.includes(value) ? [...base, value].sort((a, b) => a - b) : base;
  };

  const handleSave = async () => {
    if (duplicateIndexes.size > 0) {
      showFeedback('error', t('followUpSettings.duplicateRule', 'Deux règles ciblent le même couple transporteur/statut. Supprimez-en une avant d’enregistrer.'));
      return;
    }
    const invalidDelay = (rules || []).find((rule) => {
      const hours = Number(rule.delay_hours);
      return !Number.isInteger(hours) || hours < 1 || hours > 720;
    });
    if (invalidDelay) {
      showFeedback('error', t('followUpSettings.invalidDelay', 'Les délais doivent être compris entre 1 et 720 heures.'));
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(
        '/api/logistics/followup-config',
        {
          rules: rules.map((rule) => ({ ...rule, delay_hours: Number(rule.delay_hours) })),
          // Sent back as stored when untouched: never reset by this page.
          send_hour: sendHour === '' ? null : Number(sendHour),
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      );
      setIsDirty(false);
      showFeedback('success', t('followUpSettings.saved', 'Paramètres de relance enregistrés.'));
    } catch (error) {
      console.error('Failed to save follow-up settings:', error?.response?.status || error?.message);
      const detail = error.response?.data?.detail;
      showFeedback('error', typeof detail === 'string'
        ? detail
        : t('followUpSettings.saveError', 'Échec de l’enregistrement des paramètres de relance.'));
    } finally {
      setIsSaving(false);
    }
  };

  const loading = rules === null || mode === undefined;

  const sendHourSelect = (
    <select
      value={sendHour}
      onChange={(e) => { setSendHour(e.target.value); markDirty(); }}
      aria-label={t('followUpSettings.sendHourLabel', 'Heure d’envoi (Paris)')}
    >
      <option value="">
        {t('followUpSettings.sendHourDefault', 'Par défaut ({{hour}})', { hour: formatHour(defaultSendHour) })}
      </option>
      {SEND_HOURS.map((hour) => (
        <option key={hour} value={String(hour)}>{formatHour(hour)}</option>
      ))}
      {/* Keep an unknown stored hour selectable */}
      {sendHour !== '' && !SEND_HOURS.includes(Number(sendHour)) && (
        <option value={sendHour}>{sendHour}</option>
      )}
    </select>
  );

  // The account's own « * » rule on « en attente du transporteur » replaces
  // the 72 h default (agent_wakeup_service.account_rules).
  const ownDefault = (rules || []).find((rule) => (rule.carrier || '').trim() === ALL_CARRIERS
    && rule.status === WAITING_CARRIER);

  const agentDefaults = (
    <div className="fus-policy">
      <span className="fus-policy-title">
        {t('followUpSettings.policyTitle', 'Politique par défaut')}
        <small>{t('followUpSettings.policyHint', 'appliquée quand aucune règle ne correspond')}</small>
      </span>
      <div className="fus-policy-facts">
        <div className="fus-fact">
          <span className="fus-fact-label">{t('followUpSettings.agentDefaultCarrier', 'En attente du transporteur')}</span>
          <span className="fus-fact-value">
            {ownDefault && ownDefault.enabled === false
              ? t('followUpSettings.noFollowUp', 'pas de relance')
              : t('followUpSettings.atMostHours', '{{hours}} h au plus', {
                hours: Math.min(Number(ownDefault ? ownDefault.delay_hours : defaultDelayHours) || defaultDelayHours, agentMaxHours),
              })}
            <small> {ownDefault ? t('followUpSettings.yourRule', 'votre règle') : t('followUpSettings.byDefault', 'par défaut')}</small>
          </span>
        </div>
        <div className="fus-fact">
          <span className="fus-fact-label">{t('followUpSettings.agentOtherStatuses', 'Autres statuts')}</span>
          <span className="fus-fact-value">
            {t('followUpSettings.atMostDays', '{{days}} j au plus', { days: days(agentMaxHours) })}
            <small> {t('followUpSettings.platformLimit', 'limite de la plateforme')}</small>
          </span>
        </div>
      </div>
    </div>
  );

  const timerDefaults = (
    <div className="fus-policy">
      <span className="fus-policy-title">
        {t('followUpSettings.policyTitle', 'Politique par défaut')}
        <small>{t('followUpSettings.policyHint', 'appliquée quand aucune règle ne correspond')}</small>
      </span>
      <div className="fus-policy-facts">
        <div className="fus-fact">
          <span className="fus-fact-label">{t('followUpSettings.timersNotice', 'Avis au client')}</span>
          <span className="fus-fact-value">
            {t('followUpSettings.timersNoticeValue', 'en attente du transporteur, toutes les {{hours}} h', { hours: defaultDelayHours })}
          </span>
        </div>
        <div className="fus-fact">
          <span className="fus-fact-label">{t('followUpSettings.timersRecontact', 'Relance du transporteur muet')}</span>
          <span className="fus-fact-value">
            {t('followUpSettings.timersRecontactValue', 'tous statuts, toutes les {{hours}} h', { hours: defaultDelayHours })}
          </span>
        </div>
        <div className="fus-fact">
          <span className="fus-fact-label">
            {t('followUpSettings.sendHourTitle', 'Heure d’envoi')}
            <small>{t('followUpSettings.sendHourZone', 'Paris')}</small>
          </span>
          {sendHourSelect}
          <span className="fus-fact-note">
            {t('followUpSettings.sendHourHelp', 'une fois par jour, fenêtre d’une heure')}
          </span>
        </div>
      </div>
    </div>
  );

  // The timers that still run beside the agent: every ticket in pilot; none
  // in live (KI-311: the agent follows the tickets opened before the switch too).
  const timersAside = mode === 'pilot' ? (
    <div className="fus-timers">
      <h3>{t('followUpSettings.pilotTimersTitle', 'Relances automatiques, en parallèle pendant le pilote')}</h3>
      <p>{t('followUpSettings.pilotTimersText', 'Pendant le pilote, les minuteries continuent sur tous les tickets : avis au client et relance du transporteur qui n’a jamais répondu, une fois par jour à l’heure d’envoi, avec les délais ci-dessus comme délai entre deux relances.')}</p>
      <label className="fus-timers-hour">
        <span>{t('followUpSettings.sendHourLabel', 'Heure d’envoi (Paris)')}</span>
        {sendHourSelect}
      </label>
    </div>
  ) : null;

  const how = agentFollows ? (
    <div className="fus-how">
      <h3>{t('followUpSettings.agentHowTitle', 'Ce que fait l’agent quand un dossier attend')}</h3>
      <ol>
        <li>{t('followUpSettings.agentHow1', 'Une réponse du transporteur, un message du client ou un changement du suivi le réveillent aussitôt : ces délais ne jouent que si rien n’arrive.')}</li>
        <li>{t('followUpSettings.agentHow2', 'Sans nouvelles, il revient sur le dossier au plus tard au délai indiqué, plus tôt si le dossier le demande (le « Réveil IA » affiché sur le ticket). Il relit le dossier et le suivi du colis, puis décide.')}</li>
        <li>{t('followUpSettings.agentHow3', 'Relancer reste son jugement, sous la règle de la plateforme : un transporteur qui a répondu n’est jamais relancé pour insister ; un transporteur resté muet peut être relancé, en espaçant les relances.')}</li>
        <li><strong>{t('followUpSettings.agentHow4', 'Un délai n’est donc jamais un ordre d’écrire au transporteur ou au client.')}</strong></li>
      </ol>
    </div>
  ) : (
    <div className="fus-how">
      <h3>{t('followUpSettings.timersHowTitle', 'Ce que font les relances automatiques')}</h3>
      <ol>
        <li>{t('followUpSettings.timersHow1', 'Avis au client : tant que le dossier attend le transporteur, l’agent prévient le client que la demande suit son cours (envoi selon vos règles d’automatisation).')}</li>
        <li>{t('followUpSettings.timersHow2', 'Relance du transporteur : à un transporteur qui n’a encore jamais répondu, notre dernière demande est renvoyée (e-mail ou portail), pendant 30 jours au plus.')}</li>
        <li>{t('followUpSettings.timersHow3', 'Une fois par jour, à l’heure d’envoi, dès que le délai depuis le dernier envoi est passé.')}</li>
      </ol>
      <p className="fus-how-tip">{t('followUpSettings.timersTip', 'Pour que l’agent juge lui-même quand revenir sur un dossier et s’il faut relancer, choisissez Pilote puis Agent IA ci-dessus.')}</p>
    </div>
  );

  return (
    <div className="followup-page">
      <header className="fus-page-head">
        <h1>{t('followUpSettings.pageTitle', 'Relances')}</h1>
        <p>{t('followUpSettings.pageSubtitle', 'Quand un dossier attend la réponse d’un transporteur ou d’un client : qui y revient, au plus tard quand, et ce qu’il fait.')}</p>
      </header>

      <AgentWakeupSettings onModeChange={handleModeChange} />

      <div className="fus-card">
        {/* Header */}
        <div className="fus-card-header">
          <div className="fus-heading">
            {/* Never a mode's wording before the mode is known */}
            <h2>
              {agentFollows
                ? t('followUpSettings.agentTitle', 'Délais maximums avant réexamen')
                : mode === 'off'
                  ? t('followUpSettings.timersTitle', 'Relances automatiques')
                  : t('followUpSettings.unknownTitle', 'Délais de relance')}
            </h2>
            {(agentFollows || mode === 'off') && (
              <p>
                {agentFollows
                  ? t('followUpSettings.agentSubtitle', 'Le plus tard que l’agent reviendra sur un dossier resté sans nouvelles.')
                  : t('followUpSettings.timersSubtitle', 'Quand et sur quels dossiers les minuteries relancent.')}
              </p>
            )}
          </div>
          <div className="fus-header-actions">
            {isDirty && !isSaving && (
              <span className="fus-dirty-hint">{t('followUpSettings.unsaved', 'Modifications non enregistrées')}</span>
            )}
            <button type="button" className="u-btn u-btn--primary" onClick={handleSave} disabled={isSaving || !isDirty}>
              {isSaving
                ? (<><FaSpinner className="fus-spin" /> {t('common.saving', 'Enregistrement…')}</>)
                : t('common.save', 'Enregistrer')}
            </button>
          </div>
        </div>

        {feedback && (
          <div className={`fus-feedback ${feedback.type}`} role="status">
            {feedback.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
            {feedback.message}
          </div>
        )}

        {loading ? (
          <div className="fus-skeleton" aria-hidden="true">
            {Array.from({ length: 4 }, (_, i) => <div key={i} className="fus-skeleton-row" />)}
          </div>
        ) : (
          <>
            {how}

            {agentFollows ? agentDefaults : timerDefaults}

            {/* Rules */}
            <div className="fus-rules">
              <div className="fus-rules-header">
                <h3>
                  {agentFollows
                    ? t('followUpSettings.agentRulesTitle', 'Délais par statut et par transporteur')
                    : t('followUpSettings.timersRulesTitle', 'Règles par statut et par transporteur')}
                  {(rules || []).length > 0 && <span className="fus-rules-count">{rules.length}</span>}
                </h3>
                <p>
                  {agentFollows
                    ? t('followUpSettings.agentRulesHint', 'Une règle remplace le délai par défaut pour son statut, et pour un transporteur si vous le choisissez. Relance coupée : l’agent ne relance pas sur ce statut.')
                    : t('followUpSettings.timersRulesHint', 'Une règle remplace le défaut pour son statut, et pour un transporteur si vous le choisissez, pour l’avis comme pour la relance. Relance coupée : ni avis ni relance.')}
                </p>
              </div>

              {duplicateIndexes.size > 0 && (
                <div className="fus-dup-warning">
                  <FaExclamationTriangle />
                  {t('followUpSettings.duplicateLive', 'Des règles ciblent le même couple transporteur/statut (lignes surlignées) — la sauvegarde est bloquée.')}
                </div>
              )}

              {(rules || []).length === 0 ? (
                <div className="fus-empty">
                  {t('followUpSettings.emptyRules', 'Aucune règle : les délais par défaut ci-dessus s’appliquent.')}
                </div>
              ) : (
                <div className="fus-table-scroll">
                  <table className="fus-table">
                    <colgroup>
                      <col style={{ width: '26%' }} />
                      <col style={{ width: '30%' }} />
                      <col style={{ width: '150px' }} />
                      <col style={{ width: '24%' }} />
                      <col style={{ width: '52px' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>{t('followUpSettings.carrier', 'Transporteur')}</th>
                        <th>{t('followUpSettings.status', 'Statut du ticket')}</th>
                        <th>{t('followUpSettings.enabled', 'Relance')}</th>
                        <th>
                          {agentFollows
                            ? t('followUpSettings.agentDelay', 'Réexamen au plus tard')
                            : t('followUpSettings.timersDelay', 'Délai entre deux relances')}
                        </th>
                        <th aria-hidden="true" />
                      </tr>
                    </thead>
                    <tbody>
                      {rules.map((rule, index) => (
                        <tr key={index} className={duplicateIndexes.has(index) ? 'fus-row-dup' : ''}>
                          <td>
                            <select
                              value={rule.carrier}
                              onChange={(e) => handleRuleChange(index, 'carrier', e.target.value)}
                              aria-label={t('followUpSettings.carrier', 'Transporteur')}
                            >
                              <option value={ALL_CARRIERS}>{t('followUpSettings.allCarriers', 'Tous les transporteurs')}</option>
                              {carriers.map((name) => (
                                <option key={name} value={name}>{name}</option>
                              ))}
                              {/* Keep unknown carrier values selectable so existing rules never disappear */}
                              {rule.carrier !== ALL_CARRIERS && !carriers.includes(rule.carrier) && (
                                <option value={rule.carrier}>{rule.carrier}</option>
                              )}
                            </select>
                          </td>
                          <td>
                            <select
                              value={rule.status}
                              onChange={(e) => handleRuleChange(index, 'status', e.target.value)}
                              aria-label={t('followUpSettings.status', 'Statut du ticket')}
                            >
                              {availableStatuses.map((status) => (
                                <option key={status} value={status}>{statusLabel(status)}</option>
                              ))}
                              {/* Keep unknown status values selectable so existing rules never disappear */}
                              {rule.status && !availableStatuses.includes(rule.status) && (
                                <option value={rule.status}>{statusLabel(rule.status)}</option>
                              )}
                            </select>
                          </td>
                          <td>
                            <div className="fus-toggle-cell">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={rule.enabled !== false}
                                aria-label={t('followUpSettings.enabled', 'Relance')}
                                className={`fus-switch ${rule.enabled !== false ? 'on' : ''}`}
                                onClick={() => handleRuleChange(index, 'enabled', rule.enabled === false)}
                              >
                                <span className="fus-switch-knob" />
                              </button>
                              {rule.enabled === false && (
                                <span className="fus-off-hint">
                                  {agentFollows
                                    ? t('followUpSettings.offHint', 'aucune relance')
                                    : t('followUpSettings.offHintTimers', 'ni avis ni relance')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <select
                              className="fus-delay-select"
                              value={String(rule.delay_hours ?? '')}
                              disabled={rule.enabled === false}
                              onChange={(e) => handleRuleChange(index, 'delay_hours', Number(e.target.value))}
                              aria-label={agentFollows
                                ? t('followUpSettings.agentDelay', 'Réexamen au plus tard')
                                : t('followUpSettings.timersDelay', 'Délai entre deux relances')}
                            >
                              {!(Number(rule.delay_hours) > 0) && <option value={String(rule.delay_hours ?? '')}>—</option>}
                              {delayOptions(rule.delay_hours).map((hours) => (
                                <option key={hours} value={String(hours)}>{delayLabel(hours)}</option>
                              ))}
                            </select>
                          </td>
                          <td className="fus-cell-actions">
                            <button
                              type="button"
                              className="fus-icon-btn danger"
                              onClick={() => handleRemoveRule(index)}
                              title={t('followUpSettings.removeRule', 'Supprimer la règle')}
                              aria-label={t('followUpSettings.removeRule', 'Supprimer la règle')}
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="fus-rules-footer">
                <button type="button" className="u-btn u-btn--secondary" onClick={handleAddRule}>
                  <FaPlus /> {t('followUpSettings.addRule', 'Ajouter une règle')}
                </button>
              </div>
            </div>

            <p className="fus-instructions-note">
              <FaInfoCircle aria-hidden="true" />
              <span>{t('followUpSettings.instructionsNote', 'Ne répétez pas ces délais dans les consignes de l’agent : il les reçoit d’ici. Une consigne qui fixe un délai ou un rythme de relance contredit ce tableau dès qu’il change.')}</span>
            </p>

            {timersAside}
          </>
        )}
      </div>
    </div>
  );
};

export default FollowUpSettings;
